import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { clsx } from 'clsx';
import { Markup } from 'interweave';
import { kebabCase } from 'lodash-es';
import PropTypes from 'prop-types';

import { gqlRequest } from '@strava/api/gql-client';
import { useStravaTranslations } from '@strava/container-context';
import NavigationHelpNormalXsmall from '@strava/icons/NavigationHelpNormalXsmall';
import { Button } from '@strava/ui/Button';
import { MAP_STYLES } from '@strava/ui/Map';
import { Modal } from '@strava/ui/Modal';
import { Tooltip } from '@strava/ui/Tooltip';

import { linkableClubsQuery } from './EmbedModal.graphql';

import styles from './EmbedModal.module.scss';

// quick and dirty RadioGroup that supports non-string values
const RadioGroup = ({ options, value, onChange, name }) => {
  // The id="" attribute used by form elements and the for="" attribute used by form labels
  // needs to be globally unique, but the labels and values for these form options are not!
  //
  // If we had React 18 available, we'd use its useId() hook which is designed for this. But
  // because we don't have React 18 yet, we can roll our own without too much fuss:
  const stableUniquePrefix = useMemo(
    () => Math.random().toString(36).slice(3, 16),
    []
  );
  const optionId = (i) => `${stableUniquePrefix}-${i}`;

  return options.map(([label, v], i) => (
    <React.Fragment key={v}>
      <input
        type="radio"
        checked={v === value}
        onChange={() => onChange(v)}
        id={optionId(i)}
        name={name}
      />
      <label htmlFor={optionId(i)}>{label}</label>{' '}
    </React.Fragment>
  ));
};

// quick and dirty Select
const Select = ({ options, value, onChange }) => {
  return (
    <select value={value} onChange={(v) => onChange(v.target.value)}>
      {options.map(([label, v]) => (
        <option value={v} key={v}>
          {label}
        </option>
      ))}
    </select>
  );
};

Select.propTypes = {
  options: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

const EmbedModal = ({
  isOpen,
  onDismiss,
  embedType,
  embedId,
  // Currently, only route type embeds can be linked to Strava clubs, but it's
  // possible that we'll allow other embeddable entities to also be linked to
  // clubs in the future.
  isLinkableToClub = false,
  // Currently, only route type embeds have togglable elevation, but it's
  // possible that we'll allow other embeddable entities to in the future
  omitElevationToggle = false
}) => {
  const { t } = useStravaTranslations();
  const isRouteEmbed = embedType === 'route';
  const [isCodeCopied, setIsCodeCopied] = useState(false);
  const codeCopyDisplayTimeout = useRef();
  const staticEmbedParams = { embedType, embedId };
  const [configurableEmbedParams, setConfigurableEmbedParams] = useState({
    units: null,
    fullWidth: null,
    hideElevation: null,
    style: 'standard',
    terrain: null,
    surfaceType: null,
    mapHash: null,
    ...(isLinkableToClub && { clubId: null }),
    fromEmbed: false
  });
  const embedModalSectionRef = useRef();

  const [linkableStravaClubs, setLinkableStravaClubs] = useState([]);

  const previewContainerRef = useRef();

  // Production
  const EMBED_SCRIPT_URL = 'https://strava-embeds.com/embed.js';
  // Staging
  // const EMBED_SCRIPT_URL = 'https://staging.strava-embeds.com/embed.js';

  const updateParams = useCallback((update) => {
    const fromEmbed = Boolean(update.fromEmbed);
    setConfigurableEmbedParams((p) => ({ ...p, ...update, ...{ fromEmbed } }));
  }, []);

  useEffect(() => {
    // Global listener function that will be called by the embed when changes
    // to settings are made within it to keep the modal settings in sync.
    // See https://github.com/strava/web-embeds/blob/main/src/bootstrap/embed.js
    // eslint-disable-next-line no-underscore-dangle
    window.__STRAVA_EMBED_SETTINGS_CHANGE_LISTENER__ = (settings) =>
      updateParams({ ...settings, ...{ fromEmbed: true } });

    return () => {
      // eslint-disable-next-line no-underscore-dangle
      window.__STRAVA_EMBED_SETTINGS_CHANGE_LISTENER__ = undefined;
    };
  }, [updateParams]);

  const toggleNullParam = (param) =>
    updateParams({ [param]: configurableEmbedParams[param] ? null : true });

  const placeholderAttrString = Object.entries({
    ...staticEmbedParams,
    ...configurableEmbedParams
  })
    .filter(([, v]) => v !== null)
    .map(([k, v]) => `data-${kebabCase(k)}="${v}"`)
    .join(' ');

  const embedDivDisplay = `<div class="strava-embed-placeholder" ${placeholderAttrString}></div>`;
  const embedScriptDisplay = `<script src="${EMBED_SCRIPT_URL}"></script>`;
  const embedCodeDisplay = embedDivDisplay + embedScriptDisplay;

  useEffect(() => {
    const fetchLinkableStravaClubs = async () => {
      const gqlResponse = await gqlRequest(linkableClubsQuery);
      const me = gqlResponse?.me;
      const clubs = me?.clubs?.nodes;
      if (clubs && clubs.length) {
        setLinkableStravaClubs(clubs);
      }
    };

    if (isLinkableToClub) {
      fetchLinkableStravaClubs();
    }
  }, [isLinkableToClub]);

  useEffect(() => {
    // Don't reload the embed when the update came from the embed.
    if (!isOpen || configurableEmbedParams.fromEmbed) {
      return () => {};
    }

    const head = document.querySelector('head');
    const script = document.createElement('script');
    script.src = EMBED_SCRIPT_URL;
    script.defer = true;
    head.appendChild(script);

    if (previewContainerRef.current) {
      previewContainerRef.current.innerHTML = embedDivDisplay;
    }

    return () => {
      head.removeChild(script);
    };

    // embedDivDisplay is derived from configurableEmbedParams.
    // No need to include it in the effect dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, configurableEmbedParams]);

  function copyCode() {
    navigator.clipboard.writeText(embedCodeDisplay).then(() => {
      setIsCodeCopied(true);
      // if the user clicks the copy button before the previous timeout fires,
      // make sure to clean that up so that we can reset the clock.
      // this is a noop if "current" is null or expired
      clearTimeout(codeCopyDisplayTimeout.current);
      codeCopyDisplayTimeout.current = setTimeout(() => {
        setIsCodeCopied(false);
      }, 4000);
    });
  }

  return (
    <Modal
      open={isOpen}
      shouldRenderTriggerButton={false}
      shouldShowCloseButtonIcon={true}
      onOpenChange={(open) => {
        if (!open) {
          onDismiss();
        }
      }}
      width="100%"
      maxWidth="1104px"
    >
      <Modal.Content
        aria-labelledby="embed-modal-title"
        className={styles.embedModal}
      >
        <div ref={embedModalSectionRef} className={styles.embedModalSection}>
          <Modal.Title id="embed-modal-title">
            {t('embed_modal.create_embed')}
          </Modal.Title>
          <Modal.Body fontSize="xs">
            {isRouteEmbed && (
              <RouteEmbedConfiguration
                container={embedModalSectionRef.current}
                embedType={embedType}
                params={configurableEmbedParams}
                updateParams={updateParams}
                toggleNullParam={toggleNullParam}
                linkableStravaClubs={linkableStravaClubs}
                omitElevationToggle={omitElevationToggle}
              />
            )}
            <div className={styles.embedCodeContainer}>{embedCodeDisplay}</div>
          </Modal.Body>

          <Modal.Actions>
            <Button onClick={onDismiss}>{t('embed_modal.cancel')}</Button>
            <Button variant="primary" onClick={copyCode}>
              {isCodeCopied
                ? t('embed_modal.code_copied')
                : t('embed_modal.copy_code')}
            </Button>
          </Modal.Actions>

          <Modal.Body
            className={styles.legalTerms}
            color="extendedNeutralN2"
            fontSize="2xs"
            lineHeight="xl"
            marginTop="lg"
            marginBottom="lg"
          >
            {isRouteEmbed ? (
              <>
                <b>{t('embed_modal.additional_resources.heading')}</b>
                <ul>
                  <li>
                    <Markup
                      content={t(
                        'embed_modal.additional_resources.learn_more_html',
                        {
                          guide_url:
                            'https://partners.strava.com/resources/how-to-embed-a-strava-route'
                        }
                      )}
                    />
                  </li>
                  <li>
                    <Markup
                      content={t(
                        'embed_modal.additional_resources.by_embedding_html',
                        {
                          terms_url: 'https://www.strava.com/legal/api'
                        }
                      )}
                    />
                  </li>
                </ul>
              </>
            ) : (
              <Markup
                content={t(
                  'embed_modal.additional_resources.by_embedding_html',
                  {
                    terms_url: 'https://www.strava.com/legal/api'
                  }
                )}
              />
            )}
          </Modal.Body>
        </div>
        <div
          className={clsx(styles.embedModalSection, styles.embedPreviewSection)}
        >
          <Modal.Title>{t('embed_modal.preview')}</Modal.Title>
          <Modal.Body fontSize="xs">
            <p>
              {t('embed_modal.preview_cta')}{' '}
              {isRouteEmbed && t('embed_modal.preview_preferences_cta')}
            </p>
            <div ref={previewContainerRef}>
              <Markup noWrap={true} content={embedDivDisplay} />
            </div>
          </Modal.Body>
        </div>
      </Modal.Content>
    </Modal>
  );
};

const RouteEmbedConfiguration = ({
  container,
  params,
  updateParams,
  toggleNullParam,
  linkableStravaClubs,
  omitElevationToggle
}) => {
  const { t } = useStravaTranslations();
  const [linkedClub, setLinkedClub] = useState(null);

  // the "Auto" units option has a special tooltip with help text
  const autoUnitsLabel = (
    <span>
      {t('embed_modal.units_options.auto')}{' '}
      <Tooltip>
        <Tooltip.Trigger
          aria-label={t('embed_modal.auto_units_tooltip')}
          render={
            <NavigationHelpNormalXsmall
              tabIndex={0}
              size={12}
              style={{ opacity: 0.8 }}
            />
          }
        />
        <Tooltip.Content
          portalProps={{ container: container }}
          positionerProps={{ side: 'right' }}
          className={styles.autoTooltip}
          lineHeight="xl"
        >
          {t('embed_modal.auto_units_tooltip')}
        </Tooltip.Content>
      </Tooltip>
    </span>
  );

  // the "Responsive" width option also has special help text
  const responsiveWidthLabel = (
    <span>
      {t('embed_modal.width_options.responsive')}{' '}
      <Tooltip>
        <Tooltip.Trigger
          aria-label={t('embed_modal.responsive_width_tooltip')}
          render={
            <NavigationHelpNormalXsmall
              tabIndex={0}
              size={12}
              style={{ opacity: 0.8 }}
            />
          }
        />
        <Tooltip.Content
          portalProps={{ container: container }}
          positionerProps={{ side: 'right' }}
          className={styles.autoTooltip}
          lineHeight="xl"
        >
          {t('embed_modal.responsive_width_tooltip')}
        </Tooltip.Content>
      </Tooltip>
    </span>
  );

  return (
    <>
      {!omitElevationToggle && (
        <div className={styles.embedOption}>
          <input
            type="checkbox"
            checked={params.hideElevation === null}
            id="ele-toggle"
            onChange={() => toggleNullParam('hideElevation')}
          />{' '}
          <label htmlFor="ele-toggle">
            {t('embed_modal.show_elevation_profile')}
          </label>
        </div>
      )}
      <div className={styles.embedOption}>
        <b>{t('embed_modal.units_label')}</b>{' '}
        <RadioGroup
          options={[
            [autoUnitsLabel, null],
            [t('embed_modal.units_options.metric'), 'metric'],
            [t('embed_modal.units_options.imperial'), 'imperial']
          ]}
          value={params.units}
          onChange={(units) => updateParams({ units })}
        />
      </div>
      <div className={styles.embedOption}>
        <b>{t('embed_modal.width_label')}</b>{' '}
        <RadioGroup
          options={[
            [t('embed_modal.width_options.fixed'), null],
            [responsiveWidthLabel, true]
          ]}
          value={params.fullWidth}
          onChange={(fullWidth) => updateParams({ fullWidth })}
        />
      </div>
      <div className={styles.embedOption}>
        {/* eslint-disable-next-line jsx-a11y/label-has-for */}
        <label>
          <b>{t('embed_modal.map_style_label')}</b>{' '}
          <Select
            options={Object.keys(MAP_STYLES).map((style) => [
              t(`embed_modal.map_style.${style}.label`),
              style
            ])}
            value={params.style}
            onChange={(style) => updateParams({ style })}
          />
        </label>
      </div>
      <div className={styles.embedOption}>
        <b>{t('embed_modal.terrain_label')}</b>{' '}
        <RadioGroup
          options={[
            [t('embed_modal.terrain_options.auto'), null],
            [t('embed_modal.terrain_options.2d'), '2d'],
            [t('embed_modal.terrain_options.3d'), '3d']
          ]}
          value={params.terrain}
          onChange={(terrain) => updateParams({ terrain })}
        />
      </div>
      {!omitElevationToggle && (
        <div className={styles.embedOption}>
          <b>{t('embed_modal.surface_type_label')}</b>{' '}
          <RadioGroup
            options={[
              [t('embed_modal.surface_type_options.standard'), null],
              [t('embed_modal.surface_type_options.show_dirt'), true]
            ]}
            value={params.surfaceType}
            onChange={(surfaceType) => updateParams({ surfaceType })}
          />
        </div>
      )}
      {linkableStravaClubs.length > 0 && (
        <div className={styles.embedOption}>
          {/* eslint-disable-next-line jsx-a11y/label-has-for */}
          <label>
            <b>{t('embed_modal.club_link_label')}</b>{' '}
            <Select
              options={[
                // The 'none' string value is used instead of 'null' because
                // the onChange handler will be called with the label string
                // if 'null' is used. The label string will be translated, so
                // we can use a hard coded 'none' string and check for it in
                // the onChange handler.
                [t('embed_modal.club_link_options.none'), 'none'],
                ...linkableStravaClubs.map((club) => [club.name, club.id])
              ]}
              value={linkedClub ?? ''}
              onChange={(id) => {
                let clubId = id;
                if (clubId === 'none') {
                  clubId = null;
                }

                setLinkedClub(clubId);
                updateParams({ clubId });
              }}
            />
          </label>
        </div>
      )}
    </>
  );
};

RouteEmbedConfiguration.propTypes = {
  params: PropTypes.shape({
    units: PropTypes.oneOf([null, 'metric', 'imperial']),
    hideElevation: PropTypes.oneOf([null, true]),
    fullWidth: PropTypes.oneOf([null, true]),
    style: PropTypes.oneOf(Object.keys(MAP_STYLES)),
    terrain: PropTypes.oneOf([null, '2d', '3d']),
    surfaceType: PropTypes.oneOf([null, true])
  }).isRequired,
  updateParams: PropTypes.func.isRequired,
  toggleNullParam: PropTypes.func.isRequired,
  linkableStravaClubs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.string
    })
  ).isRequired,
  omitElevationToggle: PropTypes.bool.isRequired
};

EmbedModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  embedType: PropTypes.oneOf(['activity', 'route']).isRequired,
  embedId: PropTypes.string.isRequired,
  isLinkableToClub: PropTypes.bool,
  omitElevationToggle: PropTypes.bool
};

export default EmbedModal;
